import React, { useRef, useEffect, useState } from "react";
import Modal from "../Modal/Modal";
import {
  FileText,
  Grid3x3,
  ChevronDown,
  Trash2,
} from "lucide-react";
import "./WordProcessorModal.css";
import File from "../Icons/File";
import Cut from "../Icons/Cut";
import Copy from "../Icons/Copy";
import Paste from "../Icons/Paste";
import Undo from "../Icons/Undo";
import Redo from "../Icons/Redo";
import Search from "../Icons/Search";
import Bold from "../Icons/Bold";
import Italic from "../Icons/Italic";
import Underline from "../Icons/Underline";
import Strikethrough from "../Icons/Strikethrough";
import Subscript from "../Icons/Subscript";
import Superscript from "../Icons/Superscript";
import Grid from "../Icons/Grid";
import AlignLeft from "../Icons/AlignLeft";
import AlignCenter from "../Icons/AlignCenter";
import AlignRight from "../Icons/AlignRight";
import Justify from "../Icons/Justify";
import BulletPoint from "../Icons/BulletPoint";
import NumberedLIst from "../Icons/NumberedLIst";
import DecreaseIndent from "../Icons/DecreaseIndent";
import IncreaseIndent from "../Icons/IncreaseIndent";

interface WordProcessorModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: string;
  onChange: (content: string) => void;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  isLoading?: boolean;
}

const WordProcessorModal: React.FC<WordProcessorModalProps> = ({
  isOpen,
  onClose,
  content,
  onChange,
  onSubmit,
  isSubmitting = false,
  isLoading = false,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [showConfirmReset, setShowConfirmReset] = useState(false);
  const [showTableDropdown, setShowTableDropdown] = useState(false);
  const [showGridSelector, setShowGridSelector] = useState(false);
  const [gridHover, setGridHover] = useState({ rows: 0, cols: 0 });
  const [selectedTable, setSelectedTable] = useState<HTMLTableElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const tableButtonRef = useRef<HTMLDivElement>(null);
  const resizingRef = useRef<{
    table: HTMLElement;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
    corner: string;
  } | null>(null);

  useEffect(() => {
    if (editorRef.current && content !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = content;
    }
  }, [content]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        tableButtonRef.current &&
        !tableButtonRef.current.contains(event.target as Node)
      ) {
        setShowTableDropdown(false);
        setShowGridSelector(false);
      }
    };

    if (showTableDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTableDropdown]);

  // Track table selection
  useEffect(() => {
    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        let node = selection.anchorNode;
        while (node && node !== editorRef.current) {
          if (node.nodeName === "TABLE") {
            setSelectedTable(node as HTMLTableElement);
            return;
          }
          // Also check if we're inside a table resize container
          if (node instanceof HTMLElement && node.classList.contains('table-resize-container')) {
            const table = node.querySelector('table');
            if (table) {
              setSelectedTable(table as HTMLTableElement);
              return;
            }
          }
          node = node.parentNode;
        }
      }
      setSelectedTable(null);
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
    };
  }, []);

  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handleReset = () => {
    setShowConfirmReset(true);
  };

  const confirmReset = () => {
    if (editorRef.current) {
      editorRef.current.innerHTML = "";
      onChange("");
    }
    setShowConfirmReset(false);
  };

  const cancelReset = () => {
    setShowConfirmReset(false);
  };

  const insertTable = (rows: number, cols: number) => {
    if (!editorRef.current) return;

    const tableId = `table-${Date.now()}`;

    // Create table with resize container
    let tableHTML = `<div class="table-resize-container" style="position: relative; display: inline-block; margin: 1em 0;" data-table-id="${tableId}">`;
    tableHTML += '<table class="wp-table" style="border-collapse: collapse; position: relative;">';

    for (let i = 0; i < rows; i++) {
      tableHTML += '<tr>';
      for (let j = 0; j < cols; j++) {
        tableHTML += '<td style="border: 1px solid #dee2e6; padding: 0.5em; min-width: 50px; min-height: 30px;" contenteditable="true"><br></td>';
      }
      tableHTML += '</tr>';
    }
    tableHTML += '</table>';

    // Add resize handles at table edges
    tableHTML += '<div class="table-resize-handle table-resize-nw" data-corner="nw"></div>';
    tableHTML += '<div class="table-resize-handle table-resize-ne" data-corner="ne"></div>';
    tableHTML += '<div class="table-resize-handle table-resize-sw" data-corner="sw"></div>';
    tableHTML += '<div class="table-resize-handle table-resize-se" data-corner="se"></div>';
    tableHTML += '</div>';

    editorRef.current.focus();
    const selection = window.getSelection();
    if (selection) {
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    document.execCommand('insertHTML', false, tableHTML + '<p><br></p>');

    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
      setTimeout(() => attachResizeHandlers(), 0);
    }

    setShowTableDropdown(false);
    setShowGridSelector(false);
  };

  const deleteTable = () => {
    if (!selectedTable || !editorRef.current) return;

    // Remove the table container if it exists, otherwise remove the table itself
    const tableContainer = selectedTable.closest('.table-resize-container');
    if (tableContainer) {
      tableContainer.remove();
    } else {
      selectedTable.remove();
    }

    setSelectedTable(null);
    onChange(editorRef.current.innerHTML);
    setShowTableDropdown(false);
  };

  const handleGridHover = (row: number, col: number) => {
    setGridHover({ rows: row, cols: col });
  };

  const handleGridClick = (row: number, col: number) => {
    insertTable(row, col);
  };

  const attachResizeHandlers = () => {
    if (!editorRef.current) return;

    const handles = editorRef.current.querySelectorAll('.table-resize-handle');
    handles.forEach((handle) => {
      handle.addEventListener('mousedown', handleResizeStart as EventListener);
    });
  };

  const handleResizeStart = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const handle = e.target as HTMLElement;
    const corner = handle.dataset.corner;
    const container = handle.closest('.table-resize-container') as HTMLElement;
    const table = container?.querySelector('table') as HTMLElement;

    if (!container || !table || !corner) return;

    const rect = table.getBoundingClientRect();
    resizingRef.current = {
      table: table,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: rect.width,
      startHeight: rect.height,
      corner,
    };

    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  };

  const handleResizeMove = (e: MouseEvent) => {
    if (!resizingRef.current) return;

    const { table, startX, startY, startWidth, startHeight, corner } = resizingRef.current;
    const deltaX = e.clientX - startX;
    const deltaY = e.clientY - startY;

    let newWidth = startWidth;
    let newHeight = startHeight;

    // Minimum dimensions for the wrapper
    const minWidth = 150;
    const minHeight = 100;

    // Calculate new dimensions based on which corner is being dragged
    if (corner === 'se') {
      // Bottom-right: increase width and height
      newWidth = Math.max(minWidth, startWidth + deltaX);
      newHeight = Math.max(minHeight, startHeight + deltaY);
    } else if (corner === 'sw') {
      // Bottom-left: decrease width, increase height
      newWidth = Math.max(minWidth, startWidth - deltaX);
      newHeight = Math.max(minHeight, startHeight + deltaY);
    } else if (corner === 'ne') {
      // Top-right: increase width, decrease height
      newWidth = Math.max(minWidth, startWidth + deltaX);
      newHeight = Math.max(minHeight, startHeight - deltaY);
    } else if (corner === 'nw') {
      // Top-left: decrease both
      newWidth = Math.max(minWidth, startWidth - deltaX);
      newHeight = Math.max(minHeight, startHeight - deltaY);
    }

    table.style.width = `${newWidth}px`;
    table.style.height = `${newHeight}px`;
  };

  const handleResizeEnd = () => {
    if (resizingRef.current && editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
    resizingRef.current = null;
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
  };

  // Attach resize handlers when content loads
  useEffect(() => {
    if (editorRef.current && content) {
      attachResizeHandlers();
    }
  }, [content]);

  const headerContent = (
    <div className="flex items-center gap-2">
      <FileText size={18} />
      <span>Word Processor</span>
    </div>
  );

  return (
    <Modal
      header={headerContent}
      isOpen={isOpen}
      closeModal={onClose}
      resizable={true}
      minWidth="640px"
    >
      <div className="word-processor-container">
        {isLoading ? (
          <div style={{ padding: "2rem", textAlign: "center", minHeight: "400px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div>
              <p>Loading document...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Toolbar */}
            <div className="flex flex-col p-1 word-processor-toolbar" style={{
              backgroundImage: "linear-gradient(rgb(253, 253, 253), rgb(221, 221, 221))"
            }}>
              <div className="flex">
                <div className="toolbar-group">
                  <button
                    className="toolbar-button"
                    title="Reset"
                    onClick={handleReset}
                  >
                    <File />
                  </button>
                </div>
                {/* File operations */}
                <div className="toolbar-group">
                  <button className="toolbar-button" title="Cut" onClick={() => execCommand("cut")}>
                    <Cut />
                  </button>
                  <button className="toolbar-button" title="Copy" onClick={() => execCommand("copy")}>
                    <Copy />
                  </button>
                  <button className="toolbar-button" title="Paste" onClick={() => execCommand("paste")}>
                    <Paste />
                  </button>
                </div>
                {/* Undo/Redo */}
                <div className="toolbar-group">
                  <button className="toolbar-button" title="Undo" onClick={() => execCommand("undo")}>
                    <Undo />
                  </button>
                  <button className="toolbar-button" title="Redo" onClick={() => execCommand("redo")}>
                    <Redo />
                  </button>
                </div>
                {/* Search */}
                <div className="toolbar-group">
                  <button className="toolbar-button" title="Search">
                    <Search />
                  </button>
                </div>
                {/* Text formatting */}
                <div className="toolbar-group">
                  <button className="toolbar-button" title="Bold" onClick={() => execCommand("bold")}>
                    <Bold />
                  </button>
                  <button className="toolbar-button" title="Italic" onClick={() => execCommand("italic")}>
                    <Italic />
                  </button>
                  <button className="toolbar-button" title="Underline" onClick={() => execCommand("underline")}>
                    <Underline />
                  </button>
                  <button className="toolbar-button" title="Strikethrough" onClick={() => execCommand("strikethrough")}>
                    <Strikethrough />
                  </button>
                </div>
                {/* Subscript/Superscript */}
                <div className="toolbar-group">
                  <button className="toolbar-button" title="Subscript" onClick={() => execCommand("subscript")}>
                    <Subscript />
                  </button>
                  <button className="toolbar-button" title="Superscript" onClick={() => execCommand("superscript")}>
                    <Superscript />
                  </button>
                </div>
              </div>

              {/* Table Button */}

              <div className="flex">

                {/* Paragraph style */}
                <div className="mr-1 bg-white toolbar-group">
                  <select
                    className="toolbar-select bg-[#f7f7f7]"
                    onChange={(e) => execCommand("formatBlock", e.target.value)}
                    defaultValue="p"
                  >
                    <option value="p" className="text-[14px] bg-white mx-2">Paragraph</option>
                    <option value="h1" className="text-[30px] font-bold bg-white mx-2">Heading 1</option>
                    <option value="h2" className="text-[22px] font-bold bg-white mx-2">Heading 2</option>
                    <option value="h3" className="text-[17px] font-bold bg-white mx-2">Heading 3</option>
                    <option value="h4" className="text-[14px] font-bold bg-white mx-2">Heading 4</option>
                    <option value="h5" className="text-[12px] font-bold bg-white mx-2">Heading 5</option>
                    <option value="h6" className="text-[10px] font-bold bg-white mx-2">Heading 6</option>
                  </select>
                </div>
                <div className="toolbar-group" style={{ position: "relative" }}>
                  <div
                    ref={tableButtonRef}
                    className="toolbar-button"
                    title="Insert Table"
                    onClick={() => setShowTableDropdown(!showTableDropdown)}
                    style={{ width: "auto", padding: "0 8px", gap: "4px", cursor: "pointer", display: "flex", alignItems: "center" }}
                  >
                    <Grid />
                    <ChevronDown size={14} />
                  </div>

                  {/* Dropdown Menu */}
                  {showTableDropdown && (
                    <div
                      ref={dropdownRef}
                      className="table-dropdown"
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        marginTop: "4px",
                        backgroundColor: "white",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        zIndex: 1000,
                        minWidth: "150px",
                      }}
                    >
                      {/* Table Option */}
                      <div
                        className="table-dropdown-item"
                        onMouseEnter={() => setShowGridSelector(true)}
                        style={{
                          padding: "8px 12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: "pointer",
                          fontSize: "14px",
                          position: "relative",
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = "#f8f9fa";
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = "white";
                        }}
                      >
                        <Grid3x3 size={16} />
                        <span>Table</span>

                        {/* Grid Selector Popup */}
                        {showGridSelector && (
                          <div
                            className="grid-selector"
                            style={{
                              position: "absolute",
                              left: "100%",
                              top: 0,
                              marginLeft: "8px",
                              backgroundColor: "white",
                              border: "1px solid #dee2e6",
                              borderRadius: "4px",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                              padding: "8px",
                              zIndex: 1001,
                            }}
                          >
                            <div style={{ marginBottom: "8px", fontSize: "12px", textAlign: "center", color: "#666" }}>
                              {gridHover.rows} x {gridHover.cols}
                            </div>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(10, 20px)",
                                gap: "2px",
                              }}
                            >
                              {Array.from({ length: 100 }, (_, index) => {
                                const row = Math.floor(index / 10) + 1;
                                const col = (index % 10) + 1;
                                const isHighlighted = row <= gridHover.rows && col <= gridHover.cols;

                                return (
                                  <div
                                    key={index}
                                    onMouseEnter={() => handleGridHover(row, col)}
                                    onClick={() => handleGridClick(row, col)}
                                    style={{
                                      width: "20px",
                                      height: "20px",
                                      border: "1px solid #dee2e6",
                                      backgroundColor: isHighlighted ? "#007bff" : "white",
                                      cursor: "pointer",
                                      transition: "background-color 0.1s",
                                    }}
                                  />
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Delete Table Option */}
                      <div
                        className="table-dropdown-item"
                        onClick={selectedTable ? deleteTable : undefined}
                        style={{
                          padding: "8px 12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          cursor: selectedTable ? "pointer" : "not-allowed",
                          fontSize: "14px",
                          opacity: selectedTable ? 1 : 0.5,
                          borderTop: "1px solid #e9ecef",
                        }}
                        onMouseOver={(e) => {
                          if (selectedTable) {
                            e.currentTarget.style.backgroundColor = "#f8f9fa";
                          }
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = "white";
                        }}
                      >
                        <Trash2 size={16} />
                        <span>Delete Table</span>
                      </div>
                    </div>
                  )}
                </div>
                {/* Font size placeholder */}
                {/* <div className="toolbar-group">
                  <select className="toolbar-select-small" defaultValue="3">
                    <option value="1">8</option>
                    <option value="2">10</option>
                    <option value="3">12</option>
                    <option value="4">14</option>
                    <option value="5">18</option>
                    <option value="6">24</option>
                    <option value="7">36</option>
                  </select>
                </div> */}

                {/* Alignment */}
                <div className="toolbar-group">
                  <button className="toolbar-button" title="Align Left" onClick={() => execCommand("justifyLeft")}>
                    <AlignLeft />
                  </button>
                  <button className="toolbar-button" title="Align Center" onClick={() => execCommand("justifyCenter")}>
                    <AlignCenter />
                  </button>
                  <button className="toolbar-button" title="Align Right" onClick={() => execCommand("justifyRight")}>
                    <AlignRight  />
                  </button>
                  <button className="toolbar-button" title="Justify" onClick={() => execCommand("justifyFull")}>
                    <Justify  />
                  </button>
                </div>
                {/* Lists */}
                <div className="toolbar-group">
                  <button className="toolbar-button" title="Bullet List" onClick={() => execCommand("insertUnorderedList")}>
                    <BulletPoint />
                  </button>
                  <button className="toolbar-button" title="Numbered List" onClick={() => execCommand("insertOrderedList")}>
                    <NumberedLIst />
                  </button>
                </div>
                {/* Indent */}
                <div className="toolbar-group">
                  <button className="toolbar-button" title="Decrease Indent" onClick={() => execCommand("outdent")}>
                    <DecreaseIndent />
                  </button>
                  <button className="toolbar-button" title="Increase Indent" onClick={() => execCommand("indent")}>
                    <IncreaseIndent />
                  </button>
                </div>
              </div>
            </div>

            {/* Second toolbar row */}

            {/* Editor area */}
            <div
              ref={editorRef}
              className="word-processor-editor"
              contentEditable
              onInput={handleInput}
              suppressContentEditableWarning
            />

            {/* Submit button */}
            {onSubmit && (
              <div style={{ padding: "1rem", borderTop: "1px solid #ddd", display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                <button
                  onClick={onSubmit}
                  disabled={isSubmitting}
                  style={{
                    padding: "0.5rem 1rem",
                    backgroundColor: isSubmitting ? "#ccc" : "#007bff",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                  }}
                >
                  {isSubmitting ? "Submitting..." : "Submit Answer"}
                </button>
              </div>
            )}
          </>
        )
        }
      </div >

      {/* Confirmation Popup */}
      {
        showConfirmReset && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
            <div className="max-w-md p-6 bg-white rounded-lg shadow-lg">
              <p className="mb-6 text-sm leading-relaxed">
                Are you sure you want to reset? Resetting will remove any response you have made on this item and reset back to an unanswered state.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={confirmReset}
                  className="px-4 py-2 bg-[#0060ce] text-white border-none rounded cursor-pointer hover:bg-[#0050b3] no-underline"
                  style={{ textDecoration: 'none' }}
                >
                  OK
                </button>
                <button
                  onClick={cancelReset}
                  className="px-4 py-2 bg-[#e3e3e3] text-black border-none rounded cursor-pointer hover:bg-[#d3d3d3] no-underline"
                  style={{ textDecoration: 'none' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )
      }
    </Modal >
  );
};

export default WordProcessorModal;
